import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Avatar,
  Chip,
  Alert,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import poolwerxLogo from '../../assets/poolwerx-logo.png'
import { getProfileImageUrl, getUserInitials } from '../../utils/profileImage'
import { getRoleDisplayName, getRoleHexColor } from '../../utils/roleDisplay'
import { generateInspectorUrl } from '../../utils/urls'

const RegistrationSuccess: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, loading } = useAuth()
  const [redirectCountdown, setRedirectCountdown] = useState(5)

  const fpCode = searchParams.get('fp_code')
  const franchiseName = searchParams.get('franchise_name')

  useEffect(() => {
    // Auto-redirect countdown
    const timer = setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          handleContinue()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const handleContinue = () => {
    if (user?.role === 'FP_USER') {
      if (fpCode) {
        // No FP id at hand here (only the invitation's fp_code), so this
        // emits the bare alias - the inspector resolves it fine.
        window.location.href = `${generateInspectorUrl(fpCode)}?auto_login=true`
      } else {
        navigate('/choose-franchise')
      }
    } else if (user?.role === 'PSC_TEAM') {
      navigate('/choose-franchise')
    } else {
      navigate('/admin/franchise-partners')
    }
  }

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    )
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5',
        py: 8
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={4} alignItems="center">
          {/* Logo */}
          <Box sx={{ textAlign: 'center' }}>
            <img 
              src={poolwerxLogo} 
              alt="Poolwerx" 
              style={{ height: '80px', marginBottom: '16px' }}
            />
            <Typography 
              variant="h4" 
              fontWeight={700}
              sx={{ 
                color: '#007f98',
                textAlign: 'center',
                mb: 1
              }}
            >
              Inspector Tool
            </Typography>
          </Box>

          {/* Success Card */}
          <Card sx={{ width: '100%', boxShadow: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={3} alignItems="center">
                <Avatar sx={{ bgcolor: '#4caf50', width: 80, height: 80 }}>
                  <CheckCircleIcon sx={{ fontSize: 40 }} />
                </Avatar>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={600} sx={{ mb: 1, color: '#4caf50' }}>
                    Welcome to Poolwerx!
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Your account has been successfully created
                  </Typography>
                </Box>

                {user && (
                  <Card sx={{ bgcolor: '#f8f9fa', width: '100%' }}>
                    <CardContent>
                      <Stack spacing={2} alignItems="center">
                        <Typography variant="h6" fontWeight={600}>
                          Account Details
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar 
                            src={getProfileImageUrl(user)} 
                            sx={{ width: 48, height: 48 }}
                            slotProps={{
                              img: {
                                onError: (e: any) => {
                                  e.currentTarget.style.display = 'none'
                                }
                              }
                            }}
                          >
                            {getUserInitials(user)}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight={600}>
                              {user.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Stack>
                        <Chip 
                          label={getRoleDisplayName(user.role)}
                          sx={{ 
                            bgcolor: getRoleHexColor(user.role),
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {fpCode && franchiseName && (
                  <Card sx={{ bgcolor: '#e3f2fd', width: '100%' }}>
                    <CardContent>
                      <Stack spacing={1} alignItems="center">
                        <BusinessIcon sx={{ fontSize: 32, color: '#007f98' }} />
                        <Typography variant="body1" fontWeight={600}>
                          Access Granted to:
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#007f98' }}>
                          {fpCode}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {franchiseName}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                <Stack spacing={2} sx={{ width: '100%' }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={handleContinue}
                    sx={{
                      bgcolor: '#007f98',
                      '&:hover': { bgcolor: '#005662' },
                      py: 1.5,
                      fontSize: '1.1rem'
                    }}
                    fullWidth
                  >
                    {user?.role === 'FP_USER' ? (fpCode ? 'Go to Inspector' : 'Choose Franchise') : (user?.role === 'PSC_TEAM' ? 'Choose Franchise' : 'Go to Dashboard')}
                  </Button>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    Redirecting automatically in {redirectCountdown} seconds...
                  </Typography>
                </Stack>

                {user?.role === 'FP_USER' ? (
                  <Alert severity="info" sx={{ width: '100%' }}>
                    <Typography variant="body2">
                      <strong>Welcome to Poolwerx Inspector!</strong><br />
                      You now have access to view your franchise data, reports, and system status. 
                      You can bookmark this page for quick access in the future.
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ width: '100%' }}>
                    <Typography variant="body2">
                      <strong>Welcome to the Support Console!</strong><br />
                      You now have access to the admin dashboard where you can monitor franchise partners, 
                      view system health, and manage data exceptions across the Poolwerx network.
                    </Typography>
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Footer */}
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            If you have any questions or need assistance, please contact your administrator.
          </Typography>
        </Stack>
      </Container>
    </Box>
  )
}

export default RegistrationSuccess